import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Camera as CameraIcon, RefreshCcw, Loader2, X, ChevronLeft, ChevronRight,
  Sun, Moon, ImageOff, AlertCircle, Settings2, Plus, Trash2,
} from "lucide-react";

const CameraPage = () => {
  const { token, user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | granted | denied
  const [denyReason, setDenyReason] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [tagFilter, setTagFilter] = useState("all"); // all | day | night
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [showAccessManager, setShowAccessManager] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const userRole = user?.vloga;

  // Check access on mount
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const access = await axios.get(`${API}/cameras/access-check`, { headers: authHeaders });
        if (access.data.has_access) {
          setStatus("granted");
        } else {
          setStatus("denied");
          setDenyReason(access.data.reason);
        }
      } catch (e) {
        setStatus("denied");
        setDenyReason("network");
      }
    };
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cameras when access granted
  useEffect(() => {
    if (status !== "granted") return;
    const loadCams = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/cameras/list`, { headers: authHeaders });
        const cams = r.data.cameras || [];
        setCameras(cams);
        if (cams.length > 0) setSelectedCamera(cams[0]);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Napaka pri branju kamer");
      } finally {
        setLoading(false);
      }
    };
    loadCams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Load photos when selected camera changes or date filter changes
  useEffect(() => {
    if (!selectedCamera) return;
    const loadPhotos = async () => {
      setLoading(true);
      try {
        const body = {
          camera_ids: [selectedCamera._id || selectedCamera.id],
          limit: 200,
        };
        const r = await axios.post(`${API}/cameras/photos`, body, { headers: authHeaders });
        setPhotos(r.data.photos || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Napaka pri branju fotografij");
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCamera]);

  // Filter photos by date + tag
  const filteredPhotos = useMemo(() => {
    return photos.filter((p) => {
      // Date filter
      if (dateFrom && p.date) {
        const pDate = new Date(p.date);
        const fromDate = new Date(dateFrom);
        if (pDate < fromDate) return false;
      }
      // Tag filter
      if (tagFilter !== "all") {
        const tags = Array.isArray(p.tag) ? p.tag.map((t) => (t || "").toLowerCase()) : [(p.tag || "").toLowerCase()];
        if (!tags.includes(tagFilter)) return false;
      }
      return true;
    });
  }, [photos, dateFrom, tagFilter]);

  // Lightbox navigation
  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(-1);
  const navLightbox = (dir) => {
    const next = lightboxIdx + dir;
    if (next >= 0 && next < filteredPhotos.length) setLightboxIdx(next);
  };

  const refresh = () => {
    if (selectedCamera) setSelectedCamera({ ...selectedCamera }); // trigger reload
  };

  const isAdmin = ["super_admin", "predsednik", "gospodar", "tajnik"].includes(userRole);

  // === RENDER ===

  if (status === "checking") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "denied") {
    return (
      <Card className="max-w-2xl mx-auto mt-8" data-testid="camera-access-denied">
        <CardContent className="p-8 text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-slate-800">Nimate dostopa do kamer</h2>
          <p className="text-sm text-slate-600">
            {denyReason === "no_phone" && (
              <>V svojem profilu nimate vpisane telefonske številke. Prosite skrbnika, da vam jo doda.</>
            )}
            {denyReason === "not_in_list" && (
              <>Vaša telefonska številka ni na seznamu za dostop do kamer. Prosite skrbnika za dodajanje.</>
            )}
            {denyReason === "network" && <>Napaka pri preverjanju dostopa. Poskusite znova.</>}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="camera-page">
      {/* Camera status card */}
      <Card className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white border-0 shadow-lg">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
            <CameraIcon className="h-8 w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold truncate" data-testid="camera-title">
              {selectedCamera?.config?.name || selectedCamera?.name || "Kamera"}
            </h1>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-emerald-100">
              {selectedCamera?.status?.lastUpdate && (
                <span>Zadnja sinhronizacija: {new Date(selectedCamera.status.lastUpdate).toLocaleString("sl-SI")}</span>
              )}
              {selectedCamera?.status?.batteries && (
                <span>· 🔋 {selectedCamera.status.batteries.join("% / ")}%</span>
              )}
              {selectedCamera?.status?.signal?.processed?.percentage != null && (
                <span>· 📶 {selectedCamera.status.signal.processed.percentage}%</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={refresh} data-testid="cam-refresh">
              <RefreshCcw className="h-4 w-4 mr-1" />
              Osveži
            </Button>
            {isAdmin && (
              <Button size="sm" variant="secondary" onClick={() => setShowAccessManager((s) => !s)} data-testid="cam-manage-access">
                <Settings2 className="h-4 w-4 mr-1" />
                Dostopi
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Camera selector (if multiple) */}
      {cameras.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {cameras.map((c) => {
            const cid = c._id || c.id;
            const isActive = (selectedCamera?._id || selectedCamera?.id) === cid;
            return (
              <button
                key={cid}
                onClick={() => setSelectedCamera(c)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition ${
                  isActive ? "bg-primary text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
                data-testid={`cam-select-${cid}`}
              >
                {c.config?.name || c.name || cid}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Od datuma:</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-40"
              data-testid="cam-date-filter"
            />
            {dateFrom && (
              <Button size="sm" variant="ghost" onClick={() => setDateFrom("")}>
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          <div className="flex gap-1.5">
            {[
              { key: "all", label: "Vse", icon: null },
              { key: "day", label: "Dan", icon: Sun },
              { key: "night", label: "Noč", icon: Moon },
            ].map(({ key, label, icon: Ic }) => (
              <button
                key={key}
                onClick={() => setTagFilter(key)}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1 transition ${
                  tagFilter === key
                    ? "bg-primary text-white"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
                data-testid={`cam-tag-${key}`}
              >
                {Ic && <Ic className="h-3.5 w-3.5" />}
                {label}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500 ml-auto">
            {filteredPhotos.length} fotografij
          </div>
        </CardContent>
      </Card>

      {/* Access manager (admin) */}
      {showAccessManager && isAdmin && (
        <AccessManager token={token} onClose={() => setShowAccessManager(false)} />
      )}

      {/* Gallery grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : filteredPhotos.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <ImageOff className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Ni fotografij, ki bi ustrezale filtru.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="cam-gallery">
          {filteredPhotos.map((p, idx) => {
            const url = p.medium?.url || p.small?.url || p.preview?.url || p.large?.url;
            const date = p.date ? new Date(p.date) : null;
            const tag = Array.isArray(p.tag) ? p.tag[0] : p.tag;
            return (
              <button
                key={p._id || p.id || idx}
                onClick={() => openLightbox(idx)}
                className="relative aspect-square bg-slate-900 rounded-lg overflow-hidden group hover:opacity-90 transition"
                data-testid={`cam-photo-${idx}`}
              >
                {url ? (
                  <img
                    src={url}
                    alt={date ? date.toLocaleString("sl-SI") : "photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500">
                    <ImageOff className="h-6 w-6" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 text-white text-[10px] flex items-center justify-between">
                  <span className="truncate">
                    {date ? date.toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit" }) : ""}
                    {date ? ` ${date.toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" })}` : ""}
                  </span>
                  {tag === "night" && <Moon className="h-3 w-3" />}
                  {tag === "day" && <Sun className="h-3 w-3" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx >= 0 && filteredPhotos[lightboxIdx] && (
        <Lightbox
          photo={filteredPhotos[lightboxIdx]}
          onClose={closeLightbox}
          onPrev={() => navLightbox(-1)}
          onNext={() => navLightbox(1)}
          hasPrev={lightboxIdx > 0}
          hasNext={lightboxIdx < filteredPhotos.length - 1}
        />
      )}
    </div>
  );
};

const Lightbox = ({ photo, onClose, onPrev, onNext, hasPrev, hasNext }) => {
  const url = photo.large?.url || photo.medium?.url || photo.small?.url;
  const date = photo.date ? new Date(photo.date) : null;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onPrev();
      else if (e.key === "ArrowRight" && hasNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);
  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={onClose} data-testid="cam-lightbox">
      <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={onClose} data-testid="cam-lightbox-close">
        <X className="h-6 w-6" />
      </button>
      {hasPrev && (
        <button
          className="absolute left-4 text-white/70 hover:text-white p-2"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          data-testid="cam-lightbox-prev"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}
      <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt="photo" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
        <div className="text-white/80 text-sm text-center mt-3">
          {date ? date.toLocaleString("sl-SI") : ""}
          {photo.tag && <span className="ml-3 opacity-70">· {Array.isArray(photo.tag) ? photo.tag.join(", ") : photo.tag}</span>}
        </div>
      </div>
      {hasNext && (
        <button
          className="absolute right-4 text-white/70 hover:text-white p-2"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          data-testid="cam-lightbox-next"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
    </div>
  );
};

const AccessManager = ({ token, onClose }) => {
  const [list, setList] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/cameras/access-list`, { headers: authHeaders })
      .then((r) => setList(r.data.camera_access || []))
      .catch(() => toast.error("Napaka pri branju seznama"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (newList) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cameras/access-list`, { camera_access: newList }, { headers: authHeaders });
      setList(r.data.camera_access);
      toast.success("Shranjeno");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    } finally {
      setSaving(false);
    }
  };

  const addPhone = () => {
    const t = newPhone.trim();
    if (!t) return;
    save([...list, t]);
    setNewPhone("");
  };
  const removePhone = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    save(next);
  };

  return (
    <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="cam-access-manager">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings2 className="h-4 w-4" />
          Seznam telefonskih številk z dostopom
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
        ) : (
          <>
            <div className="flex gap-2">
              <Input
                placeholder="npr. 041 350 782"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addPhone()}
                data-testid="cam-access-new-phone"
              />
              <Button onClick={addPhone} disabled={saving} data-testid="cam-access-add">
                <Plus className="h-4 w-4 mr-1" />
                Dodaj
              </Button>
            </div>
            {list.length === 0 ? (
              <p className="text-sm text-slate-500">Seznam je prazen. Dodajte prvo številko.</p>
            ) : (
              <ul className="divide-y divide-slate-200">
                {list.map((p, idx) => (
                  <li key={idx} className="flex items-center justify-between py-2" data-testid={`cam-access-item-${idx}`}>
                    <span className="text-sm text-slate-700 font-mono">{p}</span>
                    <Button size="sm" variant="ghost" onClick={() => removePhone(idx)} disabled={saving}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-500">
              Ujemanje po zadnjih 8 številkah — format ni pomemben (`041 350 782` = `+386 41 350 782` = `+38641350782`).
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CameraPage;
